import React, { useState, useRef, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch, faFont, faBarcode, faHashtag, faTag } from "@fortawesome/free-solid-svg-icons";
import FilterProductStyle from "../../../../assets/css/components/admin/products/FiltreProduct.css.js";

// Search field types
const SEARCH_FIELDS = {
  ALL: 'all',
  TITLE: 'title',
  SKU: 'sku',
  BARCODE: 'barcode',
  BRAND: 'brand'
};

export default function FilterProduct(props) {
  const [currentFilter, setCurrentFilter] = useState(props.filter || "");
  const [showOptions, setShowOptions] = useState(false);
  const [selectedField, setSelectedField] = useState(null);
  const inputRef = useRef(null);
  const containerRef = useRef(null);

  /**
   * Handle clicks outside to close dropdown
   */
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setShowOptions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  /**
   * Sync currentFilter when props.filter changes externally
   */
  useEffect(() => {
    setCurrentFilter(props.filter || "");
  }, [props.filter]);

  /**
   * Updates the filter value and shows options if there's text
   */
  const handleInputChange = (e) => {
    const value = e.target.value;
    setCurrentFilter(value);
    setShowOptions(value.trim().length > 0);
    setSelectedField(null); // Reset selection when typing
  };

  /**
   * Execute search with specified field
   */
  const executeSearch = (fieldType = SEARCH_FIELDS.ALL) => {
    setShowOptions(false);
    props.updateFilter(currentFilter, fieldType);
  };

  /**
   * Handle option click - search with that specific field
   */
  const handleOptionClick = (fieldType) => {
    setSelectedField(fieldType);
    executeSearch(fieldType);
  };

  /**
   * Handle Enter key - search all fields if no option selected
   */
  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      executeSearch(selectedField || SEARCH_FIELDS.ALL);
    }
    if (e.key === 'Escape') {
      setShowOptions(false);
    }
  };

  /**
   * Handle search button click
   */
  const handleSearchClick = () => {
    executeSearch(selectedField || SEARCH_FIELDS.ALL);
  };

  /**
   * Get the label for the selected field
   */
  const getFieldLabel = (field) => {
    switch (field) {
      case SEARCH_FIELDS.TITLE: return 'Nombre';
      case SEARCH_FIELDS.SKU: return 'SKU';
      case SEARCH_FIELDS.BARCODE: return 'Cod. Barras';
      case SEARCH_FIELDS.BRAND: return 'Marca';
      default: return null;
    }
  };

  return (
    <FilterProductStyle>
      <div className="search-wrapper" ref={containerRef}>
        <div className={`search-input-container ${showOptions ? 'has-options' : ''}`}>
          {selectedField && (
            <span className="selected-field-badge">
              {getFieldLabel(selectedField)}
            </span>
          )}
          <input
            ref={inputRef}
            type="text"
            className="search-input"
            placeholder="Buscar productos..."
            value={currentFilter}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onFocus={() => currentFilter.trim() && setShowOptions(true)}
          />
          <button className="search-button" onClick={handleSearchClick}>
            <FontAwesomeIcon icon={faSearch} />
          </button>
        </div>

        {showOptions && currentFilter.trim() && (
          <div className="search-options">
            <div className="search-options-header">
              Buscar "{currentFilter}" en:
            </div>
            <div 
              className="search-option" 
              onClick={() => handleOptionClick(SEARCH_FIELDS.TITLE)}
            >
              <FontAwesomeIcon icon={faFont} className="option-icon" />
              <span className="option-text">Nombre del producto</span>
              <span className="option-value">{currentFilter}</span>
            </div>
            <div 
              className="search-option" 
              onClick={() => handleOptionClick(SEARCH_FIELDS.SKU)}
            >
              <FontAwesomeIcon icon={faHashtag} className="option-icon" />
              <span className="option-text">SKU</span>
              <span className="option-value">{currentFilter}</span>
            </div>
            <div 
              className="search-option" 
              onClick={() => handleOptionClick(SEARCH_FIELDS.BARCODE)}
            >
              <FontAwesomeIcon icon={faBarcode} className="option-icon" />
              <span className="option-text">Código de Barras</span>
              <span className="option-value">{currentFilter}</span>
            </div>
            {(!props.currentPlugin || props.currentPlugin === 'odoo') && (
              <div 
                className="search-option" 
                onClick={() => handleOptionClick(SEARCH_FIELDS.BRAND)}
              >
                <FontAwesomeIcon icon={faTag} className="option-icon" />
                <span className="option-text">Marca</span>
                <span className="option-value">{currentFilter}</span>
              </div>
            )}
            <div className="search-hint">
              Presiona <kbd>Enter</kbd> para buscar en todos los campos
            </div>
          </div>
        )}
      </div>
    </FilterProductStyle>
  );
}

export { SEARCH_FIELDS };
